import {useRef, type MouseEvent, type ReactNode} from 'react';
import {ChevronRight} from 'lucide-react';
import {Button} from '@/components/app/button';
import {cn} from '@/lib/utils';

export interface HierarchyNode<T = any> {
  key?: string | number;
  label?: string;
  data?: T;
  children?: HierarchyNode<T>[];
  leaf?: boolean;
  selectable?: boolean;
  className?: string;
  icon?: string;
  expanded?: boolean;
}
export type ExpandedNodes = Record<string, boolean | undefined>;
export interface HierarchyProps<T = any> {
  nodes?: HierarchyNode<T>[];
  expanded?: ExpandedNodes;
  onExpandedChange?: (expanded: ExpandedNodes) => void;
  renderNode?: (node: HierarchyNode<T>) => ReactNode;
  renderToggle?: (
    node: HierarchyNode<T>,
    options: {expanded: boolean; onClick: (event: MouseEvent) => void},
  ) => ReactNode;
  className?: string;
}
export function Hierarchy<T>({
  nodes = [],
  expanded = {},
  onExpandedChange,
  renderNode,
  renderToggle,
  className,
}: HierarchyProps<T>) {
  const root = useRef<HTMLUListElement>(null);
  const key = (node: HierarchyNode<T>, index: number) => String(node.key ?? index);
  const focus = (item: HTMLElement | undefined) => {
    if (item) {
      root.current?.querySelectorAll<HTMLElement>('[role="treeitem"]').forEach((node) => {
        node.tabIndex = -1;
      });
      item.tabIndex = 0;
      item.focus();
    }
  };
  const render = (items: HierarchyNode<T>[], depth = 1, path = ''): ReactNode =>
    items.map((node, index) => {
      const id = key(node, index);
      const nodePath = `${path}/${id}`;
      const hasChildren = !!node.children?.length;
      const isExpanded = !!expanded[id];
      const toggle = (event: MouseEvent) => {
        event.stopPropagation();
        onExpandedChange?.({...expanded, [id]: !isExpanded});
      };
      return (
        <li
          key={nodePath}
          role="treeitem"
          tabIndex={depth === 1 && index === 0 ? 0 : -1}
          aria-expanded={hasChildren ? isExpanded : undefined}
          aria-level={depth}
          data-tree-node
          data-node-id={id}
          className={cn('outline-none focus-visible:ring-2 focus-visible:ring-ring', node.className)}
          onFocus={(event) => {
            if (event.target === event.currentTarget)
              root.current?.querySelectorAll<HTMLElement>('[role="treeitem"]').forEach((item) => {
                item.tabIndex = item === event.currentTarget ? 0 : -1;
              });
          }}
          onKeyDown={(event) => {
            if (event.target !== event.currentTarget) return;
            const visible = Array.from(root.current?.querySelectorAll<HTMLElement>('[role="treeitem"]') ?? []);
            const current = visible.indexOf(event.currentTarget);
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              focus(visible[current + (event.key === 'ArrowDown' ? 1 : -1)]);
            } else if (event.key === 'Home' || event.key === 'End') {
              event.preventDefault();
              focus(event.key === 'Home' ? visible[0] : visible.at(-1));
            } else if (event.key === 'ArrowRight') {
              event.preventDefault();
              if (hasChildren && !isExpanded) onExpandedChange?.({...expanded, [id]: true});
              else if (hasChildren) focus(visible[current + 1]);
            } else if (event.key === 'ArrowLeft') {
              event.preventDefault();
              if (hasChildren && isExpanded) onExpandedChange?.({...expanded, [id]: false});
              else focus(event.currentTarget.parentElement?.closest<HTMLElement>('[role="treeitem"]') ?? undefined);
            } else if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              const action =
                event.currentTarget.querySelector<HTMLElement>('[data-tree-action]') ??
                event.currentTarget.querySelector<HTMLElement>(
                  '[data-tree-content] [role="button"], [data-tree-content] button',
                );
              action?.click();
            }
          }}
        >
          <div data-tree-content className="flex items-start gap-1">
            {renderToggle ? (
              renderToggle(node, {expanded: isExpanded, onClick: toggle})
            ) : hasChildren ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="shrink-0 self-center"
                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${node.label ?? 'group'}`}
                onClick={toggle}
              >
                <ChevronRight className={cn('size-4 transition-transform', isExpanded && 'rotate-90')} />
              </Button>
            ) : (
              // Same width as the toggle, so leaves line up with their expandable siblings.
              <span aria-hidden className="size-8 shrink-0" />
            )}
            <div className="min-w-0 flex-1">{renderNode?.(node) ?? node.label}</div>
          </div>
          {hasChildren && isExpanded && (
            <ul role="group" data-tree-children className="ml-4 list-none p-0">
              {render(node.children!, depth + 1, nodePath)}
            </ul>
          )}
        </li>
      );
    });
  return (
    <ul role="tree" aria-label="Contents" data-tree-root ref={root} className={cn('m-0 list-none p-0', className)}>
      {render(nodes)}
    </ul>
  );
}
